/**
 * The Result page's charts, as plain option objects - separate from React so they can be tested.
 *
 * Each takes the rows the API worked out (decision 5.1) and returns what ECharts should draw. Nothing
 * here recomputes a figure; the shares, counts and node positions arrive ready.
 */

import type { Schemas } from "@/api/client";

import type { ChartOption } from "./Chart";
import { CATEGORY_COLOURS, EDGE, INK, MUTED, SEGMENT_GAP, STANCE_ORDER, axis, base, percent, stanceColour } from "./theme";

export type StanceRow = Schemas["StanceRow"];
export type DriverRow = Schemas["DriverRow"];
export type Emergence = Schemas["Emergence"];

const REACTIONS = ["Negative", "Neutral", "Positive"] as const;

/** A What if quick look, drawn as one extra bar beside the real rounds (decision 6.10). */
export type QuickLookBar = { metrics: { n: number; category_counts: Record<string, number> } };

/** Share of personas at each stance, one bar per round, stacked and normalised. */
export function stanceByRound(rows: StanceRow[], quickLook: QuickLookBar | null = null): ChartOption {
  const rounds = [...new Set(rows.map((r) => r.round))].sort((a, b) => a - b);
  const labels = rounds.map((r) => `Round ${r}`);
  // A quick look counts people as negative, neutral or positive rather than across five stances, so
  // its bar carries those three. It is named "What if" and drawn paler: never read as the result.
  const share = (category: string) =>
    quickLook ? (quickLook.metrics.category_counts[category] ?? 0) / Math.max(quickLook.metrics.n, 1) : 0;
  const preview: Record<number, number> = { [-1]: share("negative"), 0: share("neutral"), 1: share("positive") };
  const label = quickLook ? `What if · ${quickLook.metrics.n} personas` : "";
  if (quickLook) labels.push(label);

  return {
    ...base(),
    xAxis: { ...axis({ percent: true, title: "Share of personas" }), max: 1 },
    yAxis: { ...axis({ category: true }), data: labels.slice().reverse(), inverse: false },
    series: STANCE_ORDER.map((stance) => ({
      name: rows.find((r) => r.stance === stance)?.stance_label ?? String(stance),
      type: "bar" as const,
      stack: "stance",
      barWidth: 26,
      itemStyle: { color: stanceColour(stance), ...SEGMENT_GAP },
      data: labels
        .slice()
        .reverse()
        .map((name) => {
          if (quickLook && name === label) {
            const value = preview[stance] ?? 0;
            return { value, personas: Math.round(value * quickLook.metrics.n), itemStyle: { opacity: 0.55 } };
          }
          const round = Number(name.replace("Round ", ""));
          const row = rows.find((r) => r.round === round && r.stance === stance);
          return { value: row?.share ?? 0, personas: row?.personas ?? 0 };
        }),
    })),
    tooltip: {
      ...base().tooltip,
      trigger: "item",
      formatter: (p: { seriesName: string; name: string; value: number; data: { personas: number } }) =>
        `${p.name}<br/><b>${p.seriesName}</b>: ${p.data.personas} personas (${percent(p.value)})`,
    },
  };
}

/** Scenario sentences personas quoted, split by how those personas ended up. */
export function phraseDrivers(rows: DriverRow[]): ChartOption {
  const ranked = rows.slice().sort((a, b) => a.total - b.total); // ECharts draws the first at the bottom
  return {
    ...base(),
    xAxis: axis({ title: "Personas who quoted it" }),
    yAxis: {
      ...axis({ category: true }),
      data: ranked.map((d) => truncate(d.sentence)),
      axisLabel: { color: MUTED, fontSize: 11, width: 260, overflow: "truncate" },
    },
    series: REACTIONS.map((reaction) => ({
      name: reaction,
      type: "bar" as const,
      stack: "drivers",
      barWidth: 18,
      itemStyle: { color: CATEGORY_COLOURS[reaction], ...SEGMENT_GAP },
      data: ranked.map((d) => ({
        value: d[reaction.toLowerCase() as "positive" | "neutral" | "negative"],
        sentence: d.sentence,
      })),
    })),
    tooltip: {
      ...base().tooltip,
      trigger: "item",
      formatter: (p: { seriesName: string; value: number; data: { sentence: string } }) =>
        `${p.data.sentence}<br/><b>${p.seriesName}</b>: ${p.value} personas`,
    },
  };
}

/** Who follows whom at the end, positioned by the layout the API computed from the study's seed. */
export function followNetwork(emergence: Emergence): ChartOption {
  const scale = 420; // the layout arrives in roughly -1..1
  return {
    ...base(),
    legend: { ...base().legend, data: STANCE_ORDER.map((s) => labelFor(emergence, s)).filter(Boolean) as string[] },
    tooltip: {
      ...base().tooltip,
      formatter: (p: { dataType: string; data: { name?: string; id?: string; cluster?: string; stance?: string } }) =>
        p.dataType === "edge"
          ? "follows"
          : `<b>${p.data.id} · ${p.data.name}</b><br/>${p.data.stance}${p.data.cluster ? `<br/>${p.data.cluster}` : ""}`,
    },
    series: [
      {
        type: "graph" as const,
        layout: "none" as const,
        roam: true,
        draggable: true,
        symbolSize: 18,
        edgeSymbol: ["none", "arrow"],
        edgeSymbolSize: 6,
        lineStyle: { color: EDGE, width: 1.2, opacity: 0.8, curveness: 0.08 },
        label: { show: true, position: "right", color: MUTED, fontSize: 10, formatter: "{b}" },
        emphasis: { focus: "adjacency" as const, label: { color: INK } },
        categories: STANCE_ORDER.map((stance) => ({
          name: labelFor(emergence, stance) ?? String(stance),
          itemStyle: { color: stanceColour(stance) },
        })),
        data: emergence.nodes.map((node) => ({
          id: node.id,
          name: node.id,
          value: node.name,
          x: node.x * scale,
          y: -node.y * scale,
          cluster: node.cluster ?? "",
          stance: node.stance_label,
          category: STANCE_ORDER.indexOf((node.stance ?? 0) as (typeof STANCE_ORDER)[number]),
          itemStyle: { color: stanceColour(node.stance), borderColor: "#0f1115", borderWidth: 2 },
        })),
        links: emergence.edges.map((edge) => ({ source: edge.source, target: edge.target })),
      },
    ],
  };
}

function labelFor(emergence: Emergence, stance: number): string | null {
  return emergence.nodes.find((n) => n.stance === stance)?.stance_label ?? null;
}

export function truncate(text: string, length = 70): string {
  return text.length <= length ? text : `${text.slice(0, length - 1)}…`;
}
